import {createColumnHelper} from '@tanstack/react-table'
import {KTIcon} from '../../../_metronic/helpers'
import {Link} from 'react-router-dom'
import {IBalanceSheetRow} from './types'

const columnHelper = createColumnHelper<IBalanceSheetRow>()

// Apply merged header styles for the Amount column
export const applyHeaderStyles = () => {
  const styleId = 'balance-sheet-amount-header-styles'

  if (!document.getElementById(styleId)) {
    const style = document.createElement('style')
    style.id = styleId
    style.innerHTML = `
      .amount-header-container {
        position: relative;
   
        text-align: center;
      }
    `
    document.head.appendChild(style)
  }
}

export const getBalanceSheetColumns = (to?: string) => [
  // Column for Group (Parent Group and Sub Group)
  columnHelper.accessor('parentGroup', {
    header: () => <span className='px-3'>Group</span>,
    cell: ({row, getValue}) => {
      if (row.depth === 0) {
        // Parent Group row
        return (
          <div className='d-flex align-items-center max-w-125px px-3 text-dark text-hover-primary'>
            <span
              className='text-muted'
              style={{
                display: 'flex',
                alignItems: 'center',
                marginLeft: '10px',
                cursor: 'pointer',
              }}
              onClick={(e) => {
                e.stopPropagation()
                row.toggleExpanded()
              }}
            >
              {row.getIsExpanded() ? (
                <KTIcon iconName='up' className='text-dark' />
              ) : (
                <KTIcon iconName='down' className='text-dark' />
              )}
            </span>

            <Link
              to={`/transactions/trial-balance/group?group_id=${
                row.original.group_id || ''
              }&to=${to}`}
              style={{
                fontSize: '13px',
                fontWeight: '700',
              }}
              className='text-dark text-hover-primary'
            >
              {getValue()}
            </Link>
          </div>
        )
      } else if (row.depth === 1) {
        // Sub Group row
        const subGroup = row.original.subGroup
        return (
          <div
            className='d-flex align-items-center px-3 text-hover-primary'
            style={{
              fontSize: '13px',
              fontWeight: '500',
            }}
          >
            {row.getCanExpand() && (
              <span
                className='text-muted'
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  marginLeft: '10px',
                  cursor: 'pointer',
                }}
                onClick={(e) => {
                  e.stopPropagation()
                  row.toggleExpanded()
                }}
              >
                {row.getIsExpanded() ? (
                  <KTIcon iconName='up' className='text-dark' />
                ) : (
                  <KTIcon iconName='down' className='text-dark' />
                )}
              </span>
            )}
            {subGroup ? (
              <Link
                to={`/transactions/trial-balance/group?group_id=${
                  row.original.group_id || ''
                }&to=${to}`}
                className='text-dark text-hover-primary'
              >
                {subGroup}
              </Link>
            ) : (
              <span className='text-dark fw-bold'>-</span>
            )}
          </div>
        )
      } else {
        return null
      }
    },
  }),
  // Column for Ledger (Parent Ledger and Sub Ledger)
  columnHelper.accessor('parentLedger', {
    header: () => <span className='px-3'>Ledger</span>,
    cell: ({row, getValue}) => {
      // For Ledger row (depth 2)
      if (row.depth === 2) {
        const parentLedger = getValue()
        return (
          <div
            className='d-flex align-items-center px-3 text-hover-primary'
            style={{
              fontSize: '12px',
              color: '#343541',
              fontWeight: '500',
            }}
          >
            {row.getCanExpand() && row.original.children && row.original.children.length > 0 && (
              <span
                className='text-muted'
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  marginLeft: '10px',
                  cursor: 'pointer',
                }}
                onClick={(e) => {
                  e.stopPropagation()
                  row.toggleExpanded()
                }}
              >
                {row.getIsExpanded() ? (
                  <KTIcon iconName='up' className='text-dark' />
                ) : (
                  <KTIcon iconName='down' className='text-dark' />
                )}
              </span>
            )}
            <Link
              to={`/transactions/trial-balance/ledger?ledgerId=${
                row.original.ledger_id || ''
              }&to=${to}`}
              style={{
                color: '#343541',
              }}
              className='text-hover-primary'
            >
              {parentLedger}
            </Link>
          </div>
        )
      }
      // For Sub Ledger row (depth 3)
      else if (row.depth === 3) {
        // console.log(row.getParentRow())
        const subLedger = row.original.subLedger
        return (
          <div
            className='px-3 ps-8 text-hover-primary'
            style={{
              fontSize: '10px',
              color: '#444654',
              fontWeight: '500',
            }}
          >
            <Link
              to={`/transactions/trial-balance/ledger?ledgerId=${
                row.original.ledger_id || ''
              }&to=${to}`}
              className='text-hover-primary'
              style={{color: '#444654'}}
            >
              <em>{subLedger || `${row.getParentRow()?.original.parentLedger} (Parent)`}</em>
            </Link>
          </div>
        )
      }
      return null
    },
  }),
  // Column for Amount
  columnHelper.accessor('amount', {
    id: 'ledger_amount',
    header: () => <div className='amount-header-container px-3'></div>,
    cell: ({row, getValue}) => {
      let className = ''

      if (row.depth === 2) {
        className = 'text-dark fw-semibold text-end'
      } else {
        className = 'text-muted text-end'
      }

      return row.depth > 1 ? (
        <div
          className={className}
          style={
            row.depth === 2
              ? {
                  fontSize: '12px',
                  color: '#343541',
                  fontWeight: '500',
                }
              : {
                  fontSize: '10px',
                  color: '#444654',
                  fontWeight: '500',
                  fontStyle: 'italic',
                }
          }
        >
          {getValue()?.toLocaleString() || '-'}
        </div>
      ) : (
        <></>
      )
    },
  }),
  columnHelper.accessor('amount', {
    id: 'group_amount',
    header: () => <div className='amount-header-container px-3'>Amount</div>,
    cell: ({row, getValue}) => {
      let className = ''

      if (row.depth === 0) {
        className = 'text-dark text-end'
      } else if (row.depth === 1) {
        className = 'text-dark text-end'
      }

      return row.depth <= 1 ? (
        <div
          className={className}
          style={
            row.depth === 0
              ? {
                  fontSize: '13px',
                  fontWeight: '700',
                }
              : {fontSize: '13px', fontWeight: '500'}
          }
        >
          {getValue()?.toLocaleString() || '-'}
        </div>
      ) : (
        <></>
      )
    },
  }),
]
